<template>
    <teleport to="#breadcrumb">
        <ul class="navbar-nav flex-row">
            <li>
                <div class="page-header">
                    <nav class="breadcrumb-one" aria-label="breadcrumb">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="javascript:;">User</a></li>
                            <li class="breadcrumb-item active" aria-current="page"><span>Management</span></li>
                        </ol>
                    </nav>
                </div>
            </li>
        </ul>
    </teleport>

    <div class="layout-px-spacing">
        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <h3 class="mb-0">User Management</h3>
                            <div class="d-flex">
                                <button @click="openCreateModal" class="btn btn-primary me-2">
                                    <i class="fas fa-plus"></i> Add User
                                </button>
                                <select v-model="selectedCompany" class="form-select" @change="fetchUsersByCompany">
                                    <option value="">All Companies</option>
                                    <option v-for="company in companies" :value="company.id" :key="company.id">
                                        {{ company.name }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div v-if="isLoading" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>

                        <div v-else class="row">
                            <div class="col-md-6 col-lg-4 mb-4" v-for="user in users" :key="user.id">
                                <div class="card h-100 shadow-sm">
                                    <div class="card-body">
                                        <div class="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h5 class="card-title mb-1">{{ user.username }}</h5>
                                                <p class="text-muted small mb-2">{{ user.email }}</p>
                                            </div>
                                            <span class="badge bg-primary">
                                                {{ getCompanyName(user.current_company_id) }}
                                            </span>
                                        </div>

                                        <div class="mb-3">
                                            <h6 class="text-muted small mb-1">Companies</h6>
                                            <div class="d-flex flex-wrap gap-1">
                                                <span v-for="company in user?.companies" :key="company.id"
                                                    class="badge bg-info">
                                                    {{ company.name }}
                                                </span>
                                            </div>
                                        </div>

                                        <div class="mb-3">
                                            <h6 class="text-muted small mb-1">Permissions</h6>
                                            <div class="d-flex flex-wrap gap-1">
                                                <template v-if="getUserPermissions(user.id).length > 0">
                                                    <span
                                                        v-for="(permission, idx) in getUserPermissions(user.id).slice(0, 3)"
                                                        :key="idx" class="badge bg-success">
                                                        {{ permission }}
                                                    </span>
                                                    <button v-if="getUserPermissions(user.id).length > 3"
                                                        @click="openPermissionsModal(user)"
                                                        class="badge bg-secondary border-0">
                                                        +{{ getUserPermissions(user.id).length - 3 }} more
                                                    </button>
                                                </template>
                                                <span v-else class="text-muted small">No permissions assigned</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card-footer bg-transparent border-top-0 d-flex justify-content-end">
                                        <button @click="openUserModal(user)"
                                            class="btn btn-sm btn-outline-primary me-2">
                                            <i class="fas fa-cog"></i> Manage
                                        </button>
                                        <button @click="openEditModal(user)"
                                            class="btn btn-sm btn-outline-secondary me-2">
                                            <i class="fas fa-edit"></i> Edit
                                        </button>
                                        <button @click="confirmDelete(user)" class="btn btn-sm btn-outline-danger">
                                            <i class="fas fa-trash"></i> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- User Management Modal -->
        <div class="modal fade" id="userManagementModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Manage User: {{ selectedUser.username }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body" style="max-height: 60vh; overflow-y: auto;">
                        <ul class="nav nav-tabs" id="userTabs" role="tablist">
                            <li class="nav-item" role="presentation">
                                <button class="nav-link active" id="companies-tab" data-bs-toggle="tab"
                                    data-bs-target="#companies" type="button" role="tab">
                                    Companies
                                </button>
                            </li>
                            <li class="nav-item" role="presentation">
                                <button class="nav-link" id="permissions-tab" data-bs-toggle="tab"
                                    data-bs-target="#permissions" type="button" role="tab">
                                    Permissions
                                </button>
                            </li>
                        </ul>

                        <div class="tab-content pt-3" id="userTabsContent">
                            <div class="tab-pane fade show active" id="companies" role="tabpanel">
                                <div class="mb-3">
                                    <label class="form-label">Assign to Companies</label>
                                    <div class="row">
                                        <div v-for="company in companies" :key="company.id" class="col-md-6 mb-2">
                                            <div class="form-check">
                                                <input class="form-check-input" type="checkbox"
                                                    :id="`company-${company.id}`" :value="company.id"
                                                    v-model="userCompanies">
                                                <label class="form-check-label" :for="`company-${company.id}`">
                                                    {{ company.name }}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="tab-pane fade" id="permissions" role="tabpanel">
                                <div class="mb-3">
                                    <label class="form-label">Select Company</label>
                                    <select v-model="permissionCompany" class="form-select mb-3"
                                        @change="loadUserPermissions">
                                        <option value="">Select a company</option>
                                        <option v-for="company in selectedUser.companies" :value="company.id"
                                            :key="company.id">
                                            {{ company.name }}
                                        </option>
                                    </select>

                                    <div v-if="permissionCompany">
                                        <label class="form-label">Assign Permissions</label>
                                        <div class="row">
                                            <div v-for="permission in permissions" :key="permission.id"
                                                class="col-md-6 mb-2">
                                                <div class="form-check">
                                                    <input class="form-check-input" type="checkbox"
                                                        :id="`permission-${permission.id}`" :value="permission.name"
                                                        v-model="userPermissions">
                                                    <label class="form-check-label"
                                                        :for="`permission-${permission.id}`">
                                                        {{ permission.name }}
                                                        <small class="text-muted d-block">{{ permission.description
                                                            }}</small>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="alert alert-info">
                                        Please select a company to view and assign permissions
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer sticky-bottom bg-white border-top">
                        <div v-if="isLoading">
                            <button type="button" class="btn btn-success mb-3 me-3"><span
                                    class="spinner-grow text-white me-2 align-self-center loader-sm">Loading...</span>
                                Processing
                            </button>
                        </div>
                        <div v-else>
                            <button type="button" class="btn btn-secondary mb-3 me-3"
                                data-bs-dismiss="modal">Close</button>
                            <button @click="updateUserCompanies" class="btn btn-primary mb-3 me-3"
                                v-if="activeTab === 'companies'">
                                Update Companies
                            </button>
                            <button @click="updateUserPermissions" class="btn btn-primary mb-3 me-3" v-else>
                                Update Permissions
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Permissions Modal -->
        <div class="modal fade" id="permissionsModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ selectedUser.username }}'s Permissions</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="getUserPermissions(selectedUser.id).length > 0" class="d-flex flex-wrap gap-2">
                            <span v-for="(permission, idx) in getUserPermissions(selectedUser.id)" :key="idx"
                                class="badge bg-success p-2">
                                {{ permission }}
                            </span>
                        </div>
                        <div v-else class="text-center py-3 text-muted">
                            No permissions assigned to this user
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create/Edit User Modal -->
        <div class="modal fade" id="userFormModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ isEditMode ? 'Edit' : 'Create' }} User</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="submitUserForm">
                            <div class="mb-3">
                                <label class="form-label">Username</label>
                                <input type="text" class="form-control" v-model="userForm.username" required>
                            </div>
                            <div class="mb-3">
                                <label class="form-label">Email</label>
                                <input type="email" class="form-control" v-model="userForm.email" required>
                            </div>
                            <div v-if="!isEditMode" class="mb-3">
                                <label class="form-label">Password</label>
                                <input type="password" class="form-control" v-model="userForm.password" required>
                            </div>
                            <div class="mb-3">
                                <label class="form-label">Initial Company</label>
                                <select class="form-select" v-model="userForm.company_id" required>
                                    <option v-for="company in companies" :value="company.id" :key="company.id">
                                        {{ company.name }}
                                    </option>
                                </select>
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <div v-if="isLoading">
                            <button type="button" class="btn btn-success mb-3 me-3"><span
                                    class="spinner-grow text-white me-2 align-self-center loader-sm">Loading...</span>
                                Processing
                            </button>
                        </div>
                        <div v-else>
                            <button type="button" class="btn btn-secondary mb-3 me-3"
                                data-bs-dismiss="modal">Cancel</button>
                            <button type="button" class="btn btn-primary mb-3 me-3" @click="submitUserForm">
                                {{ isEditMode ? 'Update' : 'Create' }} User
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import { BASE_URL } from '@/Config/config';
import axios from 'axios';

const store = useStore();

// Reactive state
const selectedCompany = ref('');
const selectedUser = ref({ username: '', companies: [], permissions: [] });
const userCompanies = ref([]);
const userPermissions = ref([]);
const permissionCompany = ref(null);
const users = ref([]);
const companies = ref([]);
const permissions = ref([]);
const isLoading = ref(false);
const activeTab = ref('companies');
const isEditMode = ref(false);

const userForm = ref({
    username: '',
    email: '',
    password: '',
    company_id: null
});

// Modal instances
let userManagementModal = null;
let permissionsModal = null;
let userFormModal = null;

// Computed
const getUserPermissions = (userId) => {
    const user = users.value.find(u => u.id === userId);
    if (!user) return [];

    // Flatten all permissions from all companies
    const allPermissions = user.permissions?.map(p => p.name) || [];
    return [...new Set(allPermissions)]; // Remove duplicates
};

const getCompanyName = (companyId) => {
    if (!companies.value || !companyId) return 'N/A';
    const company = companies.value.find(c => c.id === companyId);
    return company ? company.name : 'N/A';
};

// Methods
const fetchAllUsers = async () => {
    try {
        isLoading.value = true;
        const response = await axios.get(`${BASE_URL}/users/all`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        users.value = response.data.users || [];
        console.log('Users loaded:', users.value);
    } catch (error) {
        console.error('Error fetching users:', error);
        showErrorAlert('Failed to fetch users');
    } finally {
        isLoading.value = false;
    }
};

const fetchUsersByCompany = async () => {
    try {
        isLoading.value = true;
        const url = selectedCompany.value
            ? `${BASE_URL}/users/get?company_id=${selectedCompany.value}`
            : `${BASE_URL}/users/all`;

        const response = await axios.get(url, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        users.value = response.data.users || response.data || [];
        console.log('Users by company loaded:', users.value);
    } catch (error) {
        console.error('Error fetching users:', error);
        showErrorAlert('Failed to fetch users');
    } finally {
        isLoading.value = false;
    }
};

const fetchCompanies = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/company`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        companies.value = response.data || [];
        console.log('Companies loaded:', companies.value);
        if (companies.value.length > 0 && !userForm.value.company_id) {
            userForm.value.company_id = companies.value[0].id;
        }
    } catch (error) {
        console.error('Error fetching companies:', error);
        showErrorAlert('Failed to fetch companies');
    }
};

const loadPermissions = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/users/permissions/index`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });
        permissions.value = response.data.permissions || [];
        console.log('Permissions loaded:', permissions.value);
    } catch (error) {
        console.error('Error fetching permissions:', error);
        showErrorAlert('Failed to fetch permissions');
    }
};

const loadUserPermissions = async () => {
    if (!permissionCompany.value || !selectedUser.value.id) return;

    try {
        const response = await axios.get(`${BASE_URL}/user/permissions`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': permissionCompany.value
            },
            params: {
                user_id: selectedUser.value.id
            }
        });
        userPermissions.value = response.data.permissions?.map(p => p.name) || [];
        console.log('User permissions loaded:', userPermissions.value);
    } catch (error) {
        console.error('Error loading user permissions:', error);
        showErrorAlert('Failed to load user permissions');
    }
};

const openUserModal = (user) => {
    selectedUser.value = { ...user };
    userCompanies.value = user.companies.map(c => c.id);
    permissionCompany.value = user.current_company_id || (user.companies[0]?.id || null);
    activeTab.value = 'companies';

    if (!userManagementModal) {
        userManagementModal = new Modal(document.getElementById('userManagementModal'));
    }
    userManagementModal.show();
};

const openPermissionsModal = (user) => {
    selectedUser.value = { ...user };

    if (!permissionsModal) {
        permissionsModal = new Modal(document.getElementById('permissionsModal'));
    }
    permissionsModal.show();
};

const openCreateModal = () => {
    isEditMode.value = false;
    userForm.value = {
        username: '',
        email: '',
        password: '',
        company_id: companies.value[0]?.id || null
    };

    if (!userFormModal) {
        userFormModal = new Modal(document.getElementById('userFormModal'));
    }
    userFormModal.show();
};

const openEditModal = (user) => {
    isEditMode.value = true;
    userForm.value = {
        username: user.username,
        email: user.email,
        password: '',
        company_id: user.current_company_id
    };
    selectedUser.value = { ...user };

    if (!userFormModal) {
        userFormModal = new Modal(document.getElementById('userFormModal'));
    }
    userFormModal.show();
};

const updateUserCompanies = async () => {
    if (userCompanies.value.length === 0) {
        showWarningAlert('Please select at least one company');
        return;
    }

    try {
        isLoading.value = true;
        await axios.post(`${BASE_URL}/users/${selectedUser.value.id}/assign-companies`, {
            company_ids: userCompanies.value
        }, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        showSuccessAlert('Companies updated successfully');
        userManagementModal.hide();
        await fetchUsersByCompany();
    } catch (error) {
        console.error('Error updating companies:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to update companies');
    } finally {
        isLoading.value = false;
    }
};

const updateUserPermissions = async () => {
    if (!permissionCompany.value) {
        showWarningAlert('Please select a company first');
        return;
    }

    try {
        isLoading.value = true;
        await axios.post(`${BASE_URL}/users/${selectedUser.value.id}/assign-permissions`, {
            permission_names: userPermissions.value,
            company_id: permissionCompany.value
        }, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        showSuccessAlert('Permissions updated successfully');
        permissionsModal.hide();
        await fetchUsersByCompany();
        await loadUserPermissions();
    } catch (error) {
        console.error('Error updating permissions:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to update permissions');
    } finally {
        isLoading.value = false;
    }
};

const submitUserForm = async () => {
    try {
        isLoading.value = true;
        if (isEditMode.value) {
            // Update user
            await axios.put(`${BASE_URL}/users/update/${selectedUser.value.id}`, {
                username: userForm.value.username,
                email: userForm.value.email,
                company_id: userForm.value.company_id
            }, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            });
            showSuccessAlert('User updated successfully');
        } else {
            // Create user
            await axios.post(`${BASE_URL}/auth/register`, {
                username: userForm.value.username,
                email: userForm.value.email,
                password: userForm.value.password,
                company_id: userForm.value.company_id
            }, {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                }
            });
            showSuccessAlert('User created successfully');
        }

        userFormModal.hide();
        fetchUsersByCompany();
    } catch (error) {
        console.error('Error submitting user form:', error);
        showErrorAlert(error.response?.data?.message || 'Failed to process user');
    } finally {
        isLoading.value = false;
    }
};

const confirmDelete = (user) => {
    Swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete ${user.username}. This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
        if (result.isConfirmed) {
            try {
                isLoading.value = true;
                await axios.delete(`${BASE_URL}/users/delete/${user.id}`, {
                    headers: {
                        'Authorization': `Bearer ${store.state.token}`,
                        'X-Company-ID': store.state.currentCompany?.id
                    }
                });
                showSuccessAlert('User deleted successfully');
                fetchUsersByCompany();
            } catch (error) {
                console.error('Error deleting user:', error);
                showErrorAlert(error.response?.data?.message || 'Failed to delete user');
            } finally {
                isLoading.value = false;
            }
        }
    });
};

// Helper functions
const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showWarningAlert = (message) => {
    Swal.fire({
        icon: 'warning',
        title: 'Warning!',
        text: message,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

// Initialize
onMounted(async () => {
    await Promise.all([fetchCompanies(), loadPermissions()]);
    await fetchAllUsers();

    // Initialize modals
    userManagementModal = new Modal(document.getElementById('userManagementModal'));
    permissionsModal = new Modal(document.getElementById('permissionsModal'));
    userFormModal = new Modal(document.getElementById('userFormModal'));

    // Tab change handler
    const tabEl = document.querySelector('#userTabs');
    tabEl?.addEventListener('shown.bs.tab', event => {
        activeTab.value = event.target.id.replace('-tab', '');
    });
});
</script>

<style scoped>
.card {
    transition: transform 0.2s, box-shadow 0.2s;
    border-radius: 10px;
    overflow: hidden;
}

.card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.card-title {
    font-weight: 600;
}

.badge {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.35em 0.65em;
}

.modal-footer.sticky-bottom {
    position: sticky;
    bottom: 0;
    z-index: 10;
}

.nav-tabs .nav-link {
    font-weight: 500;
}

.form-check-label small {
    opacity: 0.8;
    display: block;
    line-height: 1.3;
}
</style>